/**
 * One-way device-calendar export planning (#1715, epic #2344).
 *
 * Pure module: expands the patient's upcoming care schedule (appointments and
 * medication reminder occurrences) into device-calendar event plans, and diffs
 * a desired plan against previously written events so a re-run updates or
 * removes Louise-created events instead of duplicating them.
 *
 * Time semantics follow the #1796 contract direction: an appointment's
 * instant is derived from its wall-clock startDate/startTime in its own IANA
 * timeZone (startTimeUtc is not yet a reliable instant); medication schedule
 * times are patient-local wall clock resolved in the patient's timezone via
 * the same conversion used for medication logs.
 */

import { Temporal } from "@js-temporal/polyfill";
import {
  expandMedicationOccurrenceDates,
  medicationOverlapsRange,
} from "@src/packages/care/medication-occurrences";
import { isMedicationStopped } from "@src/packages/care/medications/isMedicationStopped";
import { serializeMedicationLogScheduledFor } from "@src/packages/care/medication-log-date";

/** Bounded export window: unbounded recurring events are out of scope. */
export const CALENDAR_SYNC_WINDOW_DAYS = 90;

/** Elapsed length of an exported medication reminder event. */
export const MEDICATION_EVENT_MINUTES = 15;

/** Fallback length when an appointment has no persisted end. */
export const DEFAULT_APPOINTMENT_EVENT_MINUTES = 60;

export type CalendarEventPlan = {
  /** Stable identity of the Louise source item occurrence. */
  sourceKey: string;
  title: string;
  notes?: string;
  location?: string;
  /** RFC 3339 UTC instant. */
  startInstant: string;
  /** RFC 3339 UTC instant. */
  endInstant: string;
  /** IANA zone the device calendar should display the event in. */
  timeZone?: string;
  /** Alert offsets, minutes before the event start. */
  alarmsMinutesBefore: number[];
};

export type AppointmentExportSource = {
  id: string;
  title?: string | null;
  startDate?: string | null;
  startTime?: string | null;
  endDate?: string | null;
  endTime?: string | null;
  timeZone?: string | null;
  meetingRoom?: string | null;
  isOnline?: boolean | null;
  meetingLink?: string | null;
};

export type MedicationExportSource = {
  id: string;
  startDate?: string | null;
  endDate?: string | null;
  timeTaken?: string[] | null;
  notes?: string | null;
  displayName?: string | null;
  rawMedicationName?: string | null;
  dose?: string | number | null;
  doseUnit?: string | null;
  medication?: {
    name?: string | null;
    molecule?: string | null;
  } | null;
};

export type CalendarExportWindow = {
  /** Inclusive ISO start date of the export window (patient-local today). */
  startDate: string;
  /** Inclusive ISO end date of the export window. */
  endDate: string;
  /** Events starting before this instant are skipped (RFC 3339 UTC). */
  nowInstant: string;
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_TIME = /^\d{2}:\d{2}/;

export function appointmentSourceKey(appointmentId: string): string {
  return `appt-${appointmentId}`;
}

export function medicationSourceKey(
  medicationId: string,
  date: string,
  time: string,
): string {
  return `med-${medicationId}-${date}-${time}`;
}

/**
 * Compute the patient-local export window: today through today + the bounded
 * window, in the patient's timezone so "today" never drifts a day around
 * midnight or across the date line.
 */
export function computeCalendarExportWindow(
  timezone: string,
  now: Date = new Date(),
  windowDays: number = CALENDAR_SYNC_WINDOW_DAYS,
): CalendarExportWindow {
  let today: Temporal.PlainDate;
  try {
    today = Temporal.Instant.fromEpochMilliseconds(now.getTime())
      .toZonedDateTimeISO(timezone)
      .toPlainDate();
  } catch {
    today = Temporal.Instant.fromEpochMilliseconds(now.getTime())
      .toZonedDateTimeISO("UTC")
      .toPlainDate();
  }
  return {
    startDate: today.toString(),
    endDate: today.add({ days: windowDays }).toString(),
    nowInstant: now.toISOString(),
  };
}

/**
 * Derive the UTC instant of a wall-clock date/time in an IANA zone. Legacy or
 * invalid zones fall back to the supplied patient timezone so the appointment
 * still exports at a plausible instant instead of silently disappearing.
 */
function wallClockToInstant(
  date: string,
  time: string,
  timeZone: string | null | undefined,
  fallbackTimeZone: string,
): { instant: string; resolvedZone: string } | null {
  if (!ISO_DATE.test(date) || !ISO_TIME.test(time)) return null;

  let plain: Temporal.PlainDateTime;
  try {
    plain = Temporal.PlainDateTime.from(`${date}T${time.slice(0, 5)}`);
  } catch {
    return null;
  }

  for (const zone of [timeZone?.trim(), fallbackTimeZone, "UTC"]) {
    if (!zone) continue;
    try {
      return {
        instant: plain
          .toZonedDateTime(zone)
          .toInstant()
          .toString({ smallestUnit: "second" }),
        resolvedZone: zone,
      };
    } catch {
      // Try the next candidate zone.
    }
  }
  return null;
}

function addMinutesToInstant(instant: string, minutes: number): string {
  return Temporal.Instant.from(instant)
    .add({ minutes })
    .toString({ smallestUnit: "second" });
}

function isWithinWindow(
  startInstant: string,
  date: string,
  window: CalendarExportWindow,
): boolean {
  if (date.localeCompare(window.startDate) < 0) return false;
  if (date.localeCompare(window.endDate) > 0) return false;
  return startInstant.localeCompare(window.nowInstant) >= 0;
}

/**
 * Plan device-calendar events for upcoming patient-visible appointments.
 */
export function buildAppointmentEventPlans(
  appointments: readonly AppointmentExportSource[],
  options: {
    window: CalendarExportWindow;
    patientTimeZone: string;
    alarmsMinutesBefore: number[];
    /** Untranslated strings are not allowed here; the caller passes copy. */
    fallbackTitle: string;
    onlineLocationLabel: string;
  },
): CalendarEventPlan[] {
  const plans: CalendarEventPlan[] = [];

  for (const appointment of appointments) {
    if (!appointment.startDate || !appointment.startTime) continue;

    const start = wallClockToInstant(
      appointment.startDate,
      appointment.startTime,
      appointment.timeZone,
      options.patientTimeZone,
    );
    if (!start) continue;
    if (!isWithinWindow(start.instant, appointment.startDate, options.window)) {
      continue;
    }

    let endInstant: string | null = null;
    if (appointment.endDate && appointment.endTime) {
      endInstant =
        wallClockToInstant(
          appointment.endDate,
          appointment.endTime,
          appointment.timeZone,
          options.patientTimeZone,
        )?.instant ?? null;
      if (endInstant && endInstant.localeCompare(start.instant) <= 0) {
        endInstant = null;
      }
    }

    plans.push({
      sourceKey: appointmentSourceKey(appointment.id),
      title: appointment.title?.trim() || options.fallbackTitle,
      notes: appointment.meetingLink?.trim() || undefined,
      location:
        appointment.meetingRoom?.trim() ||
        (appointment.isOnline ? options.onlineLocationLabel : undefined),
      startInstant: start.instant,
      endInstant:
        endInstant ??
        addMinutesToInstant(start.instant, DEFAULT_APPOINTMENT_EVENT_MINUTES),
      timeZone: start.resolvedZone,
      alarmsMinutesBefore: options.alarmsMinutesBefore,
    });
  }

  return plans;
}

/**
 * Plan device-calendar events for medication reminder occurrences, expanded
 * through the canonical #2223 occurrence adapter so calendar export agrees
 * with every in-app schedule surface.
 */
export function buildMedicationEventPlans(
  medications: readonly MedicationExportSource[],
  options: {
    window: CalendarExportWindow;
    patientTimeZone: string;
    alarmsMinutesBefore: number[];
    fallbackTitle: string;
  },
): CalendarEventPlan[] {
  const plans: CalendarEventPlan[] = [];
  const range = {
    startDate: options.window.startDate,
    endDate: options.window.endDate,
  };

  for (const medication of medications) {
    if (isMedicationStopped(medication.notes)) continue;
    if (!medicationOverlapsRange(medication, range)) continue;

    const times =
      medication.timeTaken && medication.timeTaken.length > 0
        ? medication.timeTaken
        : ["09:00"];
    const dates = expandMedicationOccurrenceDates(medication, range);
    const title =
      medication.displayName?.trim() ||
      medication.medication?.name?.trim() ||
      medication.rawMedicationName?.trim() ||
      medication.medication?.molecule?.trim() ||
      options.fallbackTitle;
    const notes =
      medication.dose && medication.doseUnit
        ? `${medication.dose} ${medication.doseUnit}`
        : undefined;

    for (const date of dates) {
      for (const time of times) {
        if (!ISO_TIME.test(time)) continue;
        const startInstant = serializeMedicationLogScheduledFor(
          `${date}T${time.slice(0, 5)}:00`,
          options.patientTimeZone,
        );
        if (!startInstant) continue;
        const normalizedStart = Temporal.Instant.from(startInstant).toString({
          smallestUnit: "second",
        });
        if (!isWithinWindow(normalizedStart, date, options.window)) continue;

        plans.push({
          sourceKey: medicationSourceKey(medication.id, date, time.slice(0, 5)),
          title,
          notes,
          startInstant: normalizedStart,
          endInstant: addMinutesToInstant(
            normalizedStart,
            MEDICATION_EVENT_MINUTES,
          ),
          timeZone: options.patientTimeZone,
          alarmsMinutesBefore: options.alarmsMinutesBefore,
        });
      }
    }
  }

  return plans;
}

/**
 * Content fingerprint of a plan. Stored next to the created event id so an
 * unchanged occurrence is skipped on re-sync instead of rewritten (#1715
 * idempotency criterion) without reading events back from the device store.
 */
export function calendarEventFingerprint(plan: CalendarEventPlan): string {
  const canonical = JSON.stringify([
    plan.title,
    plan.notes ?? "",
    plan.location ?? "",
    plan.startInstant,
    plan.endInstant,
    plan.timeZone ?? "",
    [...plan.alarmsMinutesBefore].sort((a, b) => a - b),
  ]);
  // djb2: tiny, stable, and collision-safe enough for change detection on a
  // bounded (≤ ~1000 event) window; a false negative only causes one
  // redundant update call.
  let hash = 5381;
  for (let i = 0; i < canonical.length; i++) {
    hash = ((hash << 5) + hash + canonical.charCodeAt(i)) >>> 0;
  }
  return `${hash.toString(36)}-${canonical.length.toString(36)}`;
}

export type SyncedEventRecord = {
  eventId: string;
  fingerprint: string;
};

export type CalendarSyncPlan = {
  toCreate: CalendarEventPlan[];
  toUpdate: { eventId: string; plan: CalendarEventPlan }[];
  /** Previously created events whose source occurrence no longer exists. */
  toDelete: { sourceKey: string; eventId: string }[];
  /** Occurrences already up to date on the device. */
  unchanged: { sourceKey: string; record: SyncedEventRecord }[];
};

/**
 * Diff the desired schedule against the events a previous sync wrote.
 * Duplicate source keys keep the first occurrence (defensive; expansion never
 * emits duplicates).
 */
export function planCalendarSync(
  desired: readonly CalendarEventPlan[],
  existing: Record<string, SyncedEventRecord>,
): CalendarSyncPlan {
  const plan: CalendarSyncPlan = {
    toCreate: [],
    toUpdate: [],
    toDelete: [],
    unchanged: [],
  };
  const seen = new Set<string>();

  for (const event of desired) {
    if (seen.has(event.sourceKey)) continue;
    seen.add(event.sourceKey);

    const record = existing[event.sourceKey];
    if (!record) {
      plan.toCreate.push(event);
    } else if (record.fingerprint === calendarEventFingerprint(event)) {
      plan.unchanged.push({ sourceKey: event.sourceKey, record });
    } else {
      plan.toUpdate.push({ eventId: record.eventId, plan: event });
    }
  }

  for (const [sourceKey, record] of Object.entries(existing)) {
    if (!seen.has(sourceKey)) {
      plan.toDelete.push({ sourceKey, eventId: record.eventId });
    }
  }

  return plan;
}
